import { InputType } from '../@types';

import type {
  StripeCardNumberElementOptions,
  StripeCardCvcElementOptions,
  StripeCardExpiryElementOptions,
} from '@stripe/stripe-js';

type DefaultCssType = Record<string, any>;

type ThemeBaseType = {
  base: DefaultCssType;
  error: DefaultCssType;
  placeholder: DefaultCssType;
  disabled: DefaultCssType;
  focus: DefaultCssType;
  hover: DefaultCssType;
};

/**
 * Filters out CSS properties that are not supported by Stripe Elements.
 *
 * @param stripeCss - An object containing CSS properties to be filtered
 * @returns A new object containing only the CSS properties that are supported by Stripe Elements
 *
 * @example
 * const css = {
 *   height: '40px',
 *   backgroundColor: '#fff',
 *   unsupportedProp: 'value'
 * };
 * const filteredCss = removeUnusedCssForStripe(css);
 * // Returns: { height: '40px', backgroundColor: '#fff' }
 */
const removeUnusedCssForStripe = (stripeCss: DefaultCssType) => {
  const stripeAllowedStyles = [
    'height',
    'backgroundColor',
    'color',
    'fontFamily',
    'fontSize',
    'fontSmoothing',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'iconColor',
    'lineHeight',
    'letterSpacing',
    'textAlign',
    'padding',
    'textDecoration',
    'textShadow',
    'textTransform',
  ];

  return Object.keys(stripeCss)
    .filter(key => stripeAllowedStyles.includes(key))
    .reduce((acc, key) => {
      acc[key] = stripeCss[key];
      return acc;
    }, {} as DefaultCssType);
};

/**
 * @description Prepares the Stripe CSS configuration object based on the provided theme.
 * @param {ThemeBaseType} baseTheme - The base theme object containing style overrides.
 * @returns {object} Stripe style configuration object.
 */
const prepareStripeCss = (baseTheme: ThemeBaseType) => ({
  style: {
    base: {
      ...removeUnusedCssForStripe(baseTheme.base),
      '::placeholder': {
        ...removeUnusedCssForStripe(baseTheme.placeholder),
      },
      ':disabled': {
        ...removeUnusedCssForStripe(baseTheme.disabled),
      },
      ':focus': {
        ...removeUnusedCssForStripe(baseTheme.focus),
      },
      ':hover': {
        ...removeUnusedCssForStripe(baseTheme.hover),
      },
    },
    invalid: {
      ...removeUnusedCssForStripe(baseTheme.error),
    },
    placeholder: {
      ...removeUnusedCssForStripe(baseTheme.placeholder),
    },
  },
});

/**
 * @description Generates Stripe Element theme options for card, cvc, and expiry fields.
 * @param {ThemeBaseType} baseTheme - The base theme object containing style overrides.
 * @returns {Record<InputType, any>} Theme options for Stripe Elements.
 */
export const generateStripeTheme = (
  baseTheme: ThemeBaseType,
): Record<InputType, any> => {
  const baseInput = prepareStripeCss(baseTheme);
  const cardInput: StripeCardNumberElementOptions = {
    showIcon: true,
    iconStyle: 'default',
    ...baseInput,
  };
  const cvcInput: StripeCardCvcElementOptions = {
    ...baseInput,
  };
  const expiryInput: StripeCardExpiryElementOptions = {
    ...baseInput,
  };

  return {
    card: cardInput,
    cvc: cvcInput,
    expiry: expiryInput,
  };
};
